/**
 * ============================================================
 *  PRODUCT CATALOG - Your Single Source of Truth
 * ============================================================
 *
 *  HOW TO UPDATE YOUR INVENTORY:
 *
 *  1. ADD A NEW PRODUCT:
 *     - Find the right category below (or create a new one)
 *     - Copy an existing product object and paste it at the end of the items array
 *     - Change the id (must be unique, e.g., 'plant-25')
 *     - Update: name, price, originalPrice (if on sale), image, badge, description
 *
 *  2. CHANGE A PRICE:
 *     - Find the product by name, update the `price` field
 *     - To add a sale: set `originalPrice` to the old price, `price` to the new price
 *     - To remove a sale: delete the `originalPrice` line, or set it to null
 *
 *  3. ADD A NEW IMAGE:
 *     - Upload your image to Cloudinary (or any image host)
 *     - Paste the URL in the `image` field
 *     - For local images: place them in /public/images/products/ and use '/images/products/filename.jpg'
 *
 *  4. ADD A NEW CATEGORY:
 *     - Copy an existing category block
 *     - Give it a unique key (e.g., 'fruit-trees')
 *     - Update: title, subtitle, icon, items
 *     - It will automatically appear on the website!
 *
 *  5. MARK OUT OF STOCK:
 *     - Set `inStock: false` on any product
 *
 *  6. FEATURE A PRODUCT ON HOMEPAGE:
 *     - Set `featured: true` on any product
 *     - Featured products appear in the "Best Sellers" section
 *
 *  See INVENTORY_GUIDE.md for detailed instructions with examples.
 * ============================================================
 */

// ─── Site Configuration ────────────────────────────────────
export const siteConfig = {
  name: 'A Green Beginning',
  tagline: 'Your One-Stop Plant Shop',
  description: 'Shop premium plants, organic seeds, and gardening essentials. Free delivery on orders above ₹499.',
  freeShippingThreshold: 499,
  currency: '₹',
  phone: '+91 98765 43210',
  email: 'support@agreenbeginning.com',
  socialLinks: {
    instagram: 'https://instagram.com/agreenbeginningofficial',
    facebook: 'https://facebook.com/k.laxmipriyanka',
    youtube: 'https://www.youtube.com/@agreenbeginningofficial',
  },
};

// ─── Hero Banner Config ────────────────────────────────────
export const heroBanners = [
  {
    id: 'hero-1',
    title: 'Fresh Plants Delivered to Your Door',
    subtitle: 'Premium quality plants, seeds & gardening essentials',
    buttonText: 'Shop Now',
    buttonLink: '/products',
    image: '/images/hero/hero-plants.jpg', // Place your hero image here
    gradient: 'linear-gradient(135deg, #0a4d2e 0%, #1a7a4a 50%, #2d8f5e 100%)',
  },
];

// ─── Promotional Banners ───────────────────────────────────
export const promoBanners = [
  {
    id: 'promo-1',
    text: '🌱 Free Shipping on orders above ₹499',
    active: true,
  },
  {
    id: 'promo-2',
    text: '🌸 New Arrivals: Spring Collection is here!',
    active: true,
  },
];

// ─── Categories ────────────────────────────────────────────
export const categories = [
  { key: 'all', label: 'All Plants', icon: '🌿', color: '#52c41a' },
  { key: 'live-plants', label: 'Live Plants', icon: '🌱', color: '#52c41a' },
  { key: 'indoor-plants', label: 'Indoor Plants', icon: '🪴', color: '#13c2c2' },
  { key: 'flowering-seeds', label: 'Flowers & Seeds', icon: '🌸', color: '#eb2f96' },
  { key: 'vegetables', label: 'Vegetables', icon: '🥬', color: '#faad14' },
  { key: 'medicinal-plants', label: 'Medicinal', icon: '🌿', color: '#389e0d' },
  { key: 'luxury-woods', label: 'Luxury Woods', icon: '🌳', color: '#8B4513' },
];

// ─── Product Catalog ───────────────────────────────────────
// Each category has: title, subtitle, icon, items[]
// Each item has: id, name, price, originalPrice?, image, badge?, description?, inStock, featured?

const productCatalog = {
  'live-plants': {
    title: 'Live Plants & Cuttings',
    subtitle: 'Healthy, ready-to-pot plants delivered fresh',
    icon: '🌱',
    items: [
      {
        id: 'lp-1',
        name: 'Money Plant Golden',
        price: 149,
        originalPrice: 199,
        image: 'https://res.cloudinary.com/dcd6y2awx/image/upload/f_auto,q_auto/v1/PlantSeller/plants/money-plant',
        badge: 'Best Seller',
        description: 'Easy to grow, air-purifying golden pothos',
        inStock: true,
        featured: true,
      },
      {
        id: 'lp-2',
        name: 'Aloe Vera Plant',
        price: 99,
        originalPrice: null,
        image: 'https://res.cloudinary.com/dcd6y2awx/image/upload/f_auto,q_auto/v1/PlantSeller/plants/aloevera',
        badge: null,
        description: 'Medicinal succulent, low maintenance',
        inStock: true,
        featured: true,
      },
      {
        id: 'lp-3',
        name: 'Sugarcane Stick',
        price: 100,
        originalPrice: null,
        image: 'https://res.cloudinary.com/dcd6y2awx/image/upload/f_auto,q_auto/v1/PlantSeller/plants/sugarcane',
        badge: null,
        description: 'Fresh sugarcane stick for planting',
        inStock: true,
        featured: false,
      },
      {
        id: 'lp-4',
        name: 'Tangled Heart Vine Cuttings',
        price: 120,
        originalPrice: 150,
        image: 'https://res.cloudinary.com/dcd6y2awx/image/upload/f_auto,q_auto/v1/PlantSeller/plants/tangled-heart',
        badge: 'Sale',
        description: 'Beautiful trailing vine for hanging pots',
        inStock: true,
        featured: false,
      },
    ],
  },

  'indoor-plants': {
    title: 'Indoor Plants',
    subtitle: 'Transform your home with beautiful greens',
    icon: '🪴',
    items: [
      {
        id: 'ip-1',
        name: 'Snake Plant (Medium)',
        price: 249,
        originalPrice: 299,
        image: 'https://res.cloudinary.com/dcd6y2awx/image/upload/f_auto,q_auto/v1/PlantSeller/plants/snake-plant',
        badge: 'Popular',
        description: 'NASA-approved air purifier, extremely low maintenance',
        inStock: true,
        featured: true,
      },
      {
        id: 'ip-2',
        name: 'Peace Lily (Small)',
        price: 179,
        originalPrice: 199,
        image: 'https://res.cloudinary.com/dcd6y2awx/image/upload/f_auto,q_auto/v1/PlantSeller/plants/peace-lily',
        badge: null,
        description: 'Elegant white flowers, thrives in low light',
        inStock: true,
        featured: true,
      },
      {
        id: 'ip-3',
        name: 'Spider Plant',
        price: 120,
        originalPrice: null,
        image: 'https://res.cloudinary.com/dcd6y2awx/image/upload/f_auto,q_auto/v1/PlantSeller/plants/spider-plant',
        badge: null,
        description: 'Fast growing, produces baby plantlets',
        inStock: true,
        featured: false,
      },
      {
        id: 'ip-4',
        name: 'Pothos Golden',
        price: 89,
        originalPrice: null,
        image: 'https://res.cloudinary.com/dcd6y2awx/image/upload/f_auto,q_auto/v1/PlantSeller/plants/pothos',
        badge: 'Budget Pick',
        description: 'Hardy trailing plant, perfect for beginners',
        inStock: true,
        featured: false,
      },
    ],
  },

  'flowering-seeds': {
    title: 'Flowers, Seeds & Bulbs',
    subtitle: 'Grow vibrant blooms from premium seeds',
    icon: '🌸',
    items: [
      {
        id: 'fs-1',
        name: 'White Lily Tuberose (1 Bulb)',
        price: 25,
        originalPrice: null,
        image: 'https://res.cloudinary.com/dcd6y2awx/image/upload/f_auto,q_auto/v1/PlantSeller/bulbs/white-lily',
        badge: null,
        description: 'Fragrant single-petal white lily bulb',
        inStock: true,
        featured: false,
      },
      {
        id: 'fs-2',
        name: 'Double Petal Lily (1 Bulb)',
        price: 100,
        originalPrice: null,
        image: 'https://res.cloudinary.com/dcd6y2awx/image/upload/f_auto,q_auto/v1/PlantSeller/bulbs/double-lily',
        badge: 'Premium',
        description: 'Stunning double-layered white lily',
        inStock: true,
        featured: true,
      },
      {
        id: 'fs-3',
        name: 'Pink Lily Tuberose (1 Bulb)',
        price: 100,
        originalPrice: null,
        image: 'https://res.cloudinary.com/dcd6y2awx/image/upload/f_auto,q_auto/v1/PlantSeller/bulbs/pink-lily',
        badge: null,
        description: 'Beautiful pink flowering bulb',
        inStock: true,
        featured: false,
      },
      {
        id: 'fs-4',
        name: 'Marigold Seeds (50 Seeds)',
        price: 30,
        originalPrice: null,
        image: 'https://res.cloudinary.com/dcd6y2awx/image/upload/f_auto,q_auto/v1/PlantSeller/seeds/marigold',
        badge: null,
        description: 'Bright orange marigold, easy to grow',
        inStock: true,
        featured: false,
      },
    ],
  },

  'vegetables': {
    title: 'Vegetable Seeds',
    subtitle: 'Grow your own fresh organic vegetables',
    icon: '🥬',
    items: [
      {
        id: 'vg-1',
        name: 'Green Beans (20 Seeds)',
        price: 40,
        originalPrice: null,
        image: 'https://res.cloudinary.com/dcd6y2awx/image/upload/f_auto,q_auto/v1/PlantSeller/seeds/green-beans',
        badge: null,
        description: 'Fast-growing bush beans, harvest in 60 days',
        inStock: true,
        featured: false,
      },
      {
        id: 'vg-2',
        name: 'Long Beans (20 Seeds)',
        price: 40,
        originalPrice: null,
        image: 'https://res.cloudinary.com/dcd6y2awx/image/upload/f_auto,q_auto/v1/PlantSeller/seeds/long-beans',
        badge: null,
        description: 'Climbing variety, prolific producer',
        inStock: true,
        featured: false,
      },
      {
        id: 'vg-3',
        name: 'Beetroot (50 Seeds)',
        price: 40,
        originalPrice: null,
        image: 'https://res.cloudinary.com/dcd6y2awx/image/upload/f_auto,q_auto/v1/PlantSeller/seeds/beetroot',
        badge: null,
        description: 'Rich in nutrients, easy to grow',
        inStock: true,
        featured: false,
      },
      {
        id: 'vg-4',
        name: 'Cherry Tomato (30 Seeds)',
        price: 35,
        originalPrice: 45,
        image: 'https://res.cloudinary.com/dcd6y2awx/image/upload/f_auto,q_auto/v1/PlantSeller/seeds/tomato',
        badge: 'Sale',
        description: 'Sweet cherry tomatoes, perfect for pots',
        inStock: true,
        featured: true,
      },
    ],
  },

  'medicinal-plants': {
    title: 'Medicinal Plants',
    subtitle: 'Natural remedies from your own garden',
    icon: '🌿',
    items: [
      {
        id: 'mp-1',
        name: 'Tulsi (Holy Basil) Plant',
        price: 50,
        originalPrice: null,
        image: 'https://res.cloudinary.com/dcd6y2awx/image/upload/f_auto,q_auto/v1/PlantSeller/plants/tulsi',
        badge: 'Must Have',
        description: 'Sacred herb with amazing health benefits',
        inStock: true,
        featured: true,
      },
      {
        id: 'mp-2',
        name: 'Ashwagandha Seeds (30 Seeds)',
        price: 60,
        originalPrice: null,
        image: 'https://res.cloudinary.com/dcd6y2awx/image/upload/f_auto,q_auto/v1/PlantSeller/seeds/ashwagandha',
        badge: null,
        description: 'Adaptogenic herb, stress reliever',
        inStock: true,
        featured: false,
      },
      {
        id: 'mp-3',
        name: 'Neem Plant (Small)',
        price: 75,
        originalPrice: 90,
        image: 'https://res.cloudinary.com/dcd6y2awx/image/upload/f_auto,q_auto/v1/PlantSeller/plants/neem',
        badge: 'Sale',
        description: 'Natural insect repellent, medicinal tree',
        inStock: true,
        featured: false,
      },
      {
        id: 'mp-4',
        name: 'Brahmi Plant',
        price: 70,
        originalPrice: null,
        image: 'https://res.cloudinary.com/dcd6y2awx/image/upload/f_auto,q_auto/v1/PlantSeller/plants/brahmi',
        badge: null,
        description: 'Memory-boosting Ayurvedic herb',
        inStock: true,
        featured: false,
      },
    ],
  },

  'luxury-woods': {
    title: 'Luxury Wood Seeds',
    subtitle: 'Invest in premium timber trees',
    icon: '🌳',
    items: [
      {
        id: 'lw-1',
        name: 'Indian Sandalwood (30 Seeds)',
        price: 200,
        originalPrice: null,
        image: 'https://res.cloudinary.com/dcd6y2awx/image/upload/f_auto,q_auto/v1/PlantSeller/seeds/sandalwood',
        badge: 'Premium',
        description: 'Fragrant, high-value timber tree',
        inStock: true,
        featured: true,
      },
      {
        id: 'lw-2',
        name: 'Red Sandalwood (20 Seeds)',
        price: 200,
        originalPrice: null,
        image: 'https://res.cloudinary.com/dcd6y2awx/image/upload/f_auto,q_auto/v1/PlantSeller/seeds/red-sandalwood',
        badge: 'Premium',
        description: 'Precious red wood, slow growing',
        inStock: true,
        featured: false,
      },
      {
        id: 'lw-3',
        name: 'Teak Wood (25 Seeds)',
        price: 180,
        originalPrice: 220,
        image: 'https://res.cloudinary.com/dcd6y2awx/image/upload/f_auto,q_auto/v1/PlantSeller/seeds/teak',
        badge: 'Sale',
        description: 'Premium hardwood, excellent investment',
        inStock: true,
        featured: false,
      },
      {
        id: 'lw-4',
        name: 'Casuarina Seeds (30 Seeds)',
        price: 75,
        originalPrice: null,
        image: 'https://res.cloudinary.com/dcd6y2awx/image/upload/f_auto,q_auto/v1/PlantSeller/seeds/casurarina',
        badge: null,
        description: 'Fast-growing coastal tree',
        inStock: true,
        featured: false,
      },
    ],
  },
};

// ─── Helper Functions (DO NOT MODIFY) ──────────────────────

/** Get all products as a flat array */
export const getAllCatalogProducts = () => {
  const products = [];
  Object.entries(productCatalog).forEach(([categoryKey, category]) => {
    category.items.forEach((item) => {
      products.push({
        ...item,
        category: categoryKey,
        categoryTitle: category.title,
      });
    });
  });
  return products;
};

/** Get featured products */
export const getFeaturedProducts = () => {
  return getAllCatalogProducts().filter((p) => p.featured && p.inStock);
};

/** Get products by category key */
export const getProductsByCategory = (categoryKey) => {
  if (categoryKey === 'all') return getAllCatalogProducts();
  return productCatalog[categoryKey]?.items.map((item) => ({
    ...item,
    category: categoryKey,
    categoryTitle: productCatalog[categoryKey].title,
  })) || [];
};

/** Calculate discount percentage */
export const getDiscountPercent = (item) => {
  if (!item.originalPrice || item.originalPrice <= item.price) return 0;
  return Math.round(((item.originalPrice - item.price) / item.originalPrice) * 100);
};

/** Search products by keyword */
export const searchCatalogProducts = (keyword) => {
  if (!keyword) return getAllCatalogProducts();
  const lower = keyword.toLowerCase();
  return getAllCatalogProducts().filter(
    (p) =>
      p.name.toLowerCase().includes(lower) ||
      p.description?.toLowerCase().includes(lower) ||
      p.category.toLowerCase().includes(lower)
  );
};

export default productCatalog;
